/**
 * Paper Trading Tab Lease Management.
 *
 * Ensures only one browser tab actively places/matches paper orders at a time,
 * while background/secondary tabs display a read-only banner with a takeover action.
 */

export interface TabLeasePayload {
  id: string;
  ts: number;
}

export const DEFAULT_TAB_LEASE_TTL_MS = 6000;

/**
 * Reads and parses the current tab lease record from storage.
 */
export function readTabLease(
  storageKey: string,
  storage: Pick<Storage, "getItem"> = typeof localStorage !== "undefined"
    ? localStorage
    : { getItem: () => null },
): TabLeasePayload | null {
  try {
    const raw = storage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

/**
 * Writes a tab lease record to storage for the given tab ID.
 */
export function writeTabLease(
  storageKey: string,
  tabId: string,
  nowMs: number = Date.now(),
  storage: Pick<Storage, "setItem"> = typeof localStorage !== "undefined"
    ? localStorage
    : { setItem: () => {} },
): void {
  try {
    storage.setItem(storageKey, JSON.stringify({ id: tabId, ts: nowMs }));
  } catch (e) {}
}

/**
 * Returns the tab ID of the current active lease holder, or null if expired/unheld.
 */
export function getTabLeaseHolder(
  storageKey: string,
  ttlMs: number = DEFAULT_TAB_LEASE_TTL_MS,
  nowMs: number = Date.now(),
  storage: Pick<Storage, "getItem"> = typeof localStorage !== "undefined"
    ? localStorage
    : { getItem: () => null },
): string | null {
  const lease = readTabLease(storageKey, storage);
  if (!lease || !lease.id || !lease.ts) return null;
  if (nowMs - lease.ts > ttlMs) return null; // holder went away / expired
  return lease.id;
}

/**
 * Evaluates whether the given tab ID is the active lease holder.
 */
export function evaluateTabLeaseState(
  storageKey: string,
  tabId: string,
  ttlMs: number = DEFAULT_TAB_LEASE_TTL_MS,
  nowMs: number = Date.now(),
  storage: Pick<Storage, "getItem"> = typeof localStorage !== "undefined"
    ? localStorage
    : { getItem: () => null },
): { isHolder: boolean; holderId: string | null } {
  const holderId = getTabLeaseHolder(storageKey, ttlMs, nowMs, storage);
  const isHolder = !holderId || holderId === tabId;
  return { isHolder, holderId };
}
